import { useMemo, useState } from "react";
import { Box, Flex, Spinner, Text, VStack } from "@chakra-ui/react";
import { MdOutlineAttachMoney } from "react-icons/md";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import {
  BuscarVinculosEncerradosParams,
  IVinculosEncerradosRetorno,
} from "@/models/consignado/proposal-billings.model";
import { buscarVinculosEncerrados } from "@/services/consignado/proposta-cobranca.service";
import ComoUtilizarModal, {
  CONCEITOS_ESCRITURACAO_REPASSE,
  SLIDES_RELATORIOS_PARCELAS,
  SLIDE_GERACAO_BOLETOS,
} from "../VinculosAtivosRedesign/ComoUtilizarModal";
import VinculosFiltersBar, {
  VinculosFiltrosDraft,
} from "../VinculosAtivosRedesign/VinculosFiltersBar";
import ReprocessarCobrancasButton from "../VinculosAtivosRedesign/ReprocessarCobrancasButton";
import CcbRow from "../VinculosAtivosRedesign/CcbRow";
import Pagination from "../VinculosAtivosRedesign/Pagination";
import { VINCULOS_ENCERRADOS_QUERY_KEY } from "../VinculosAtivosRedesign/queryKeys";
import StatusFilterBarEncerrados from "./StatusFilterBarEncerrados";
import { recorteDoStatusFiltroEncerrado, StatusFiltroEncerrado } from "./statusEncerrado";
import { hidratarCcbEncerrado } from "./hidratarCcbEncerrado";

const FILTROS_INICIAIS: VinculosFiltrosDraft = {
  tomador: "",
  empresa: "",
  ccb: "",
  ordenacao: "DESC",
  renegociacao: "",
};

const PAGE_SIZE_INICIAL = 10;

// Texto do protótipo (feat/storybook VinculosEncerradosRedesign). Grounded em
// portais e aplicações/ADM GT/CLT - consignado/cobrança/Cobrança (ADM GT).md —
// ações reais da tela "Vínculos Encerrados" (comprovante de desligamento,
// novos vínculos, renegociação por término).
const PASSOS_COMO_UTILIZAR = [
  {
    titulo: "Lista sem agrupamento por empresa",
    descricao:
      "O vínculo empregatício já terminou, então a listagem é organizada direto por tomador/CCB, sem o nível de empresa.",
  },
  {
    titulo: "Filtros de elegibilidade ao seguro",
    descricao:
      "Classificam cada vínculo (Sem seguro, Não elegível ao seguro, Elegível parcial e 100% Elegível com VE ≥ 30 dias). O motivo aparece como badge na própria linha.",
  },
  {
    titulo: "Motivo e data do desligamento",
    descricao: "Badges na linha mostram por que o vínculo foi encerrado e quando.",
  },
  {
    titulo: "Término de Vínculo e Motivo do Desligamento",
    descricao:
      "Expanda o vínculo para ver os detalhes completos e gerar o comprovante de desligamento.",
  },
  {
    titulo: "Novos Vínculos",
    descricao:
      "Veja se o CPF do tomador já tem um novo vínculo empregatício elegível para retomar o desconto em folha.",
  },
  {
    titulo: "Simule e realize a renegociação",
    descricao:
      "Simule o parcelamento, gere a proposta e acompanhe o status da averbação junto ao banco.",
  },
  {
    titulo: "Relatórios de parcelas e quitação",
    descricao:
      "Na seção Parcelas do vínculo, exporte em PDF a situação das parcelas (por período) e o relatório de quitação (parcelas, data de cálculo e total a quitar). Veja os slides de cada relatório.",
  },
];

// Redesign de "Vínculos Encerrados" — lista PLANA de CCBs (sem nível de
// empresa: o vínculo empregatício já terminou). Reaproveita CcbRow /
// paginação / filtros de texto do redesign de Vínculos Ativos. Filtro,
// ordenação e paginação são server-side (GET /cobrancas/vinculos-encerrados);
// o filtro rápido vai como `recorte` e a elegibilidade chega classificada.
export default function VinculosEncerradosRedesign() {
  const queryClient = useQueryClient();
  const backgroundStyle = useConditionalBackground();
  const [statusFiltro, setStatusFiltro] = useState<StatusFiltroEncerrado>("TODOS");
  const [filtrosDraft, setFiltrosDraft] = useState<VinculosFiltrosDraft>(FILTROS_INICIAIS);
  const [filtrosAplicados, setFiltrosAplicados] = useState<VinculosFiltrosDraft>(FILTROS_INICIAIS);
  const [pagina, setPagina] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_INICIAL);

  const params: BuscarVinculosEncerradosParams = useMemo(
    () => ({
      pagina,
      limite: pageSize,
      order: filtrosAplicados.ordenacao,
      tomador: filtrosAplicados.tomador || undefined,
      codigo_proposta: filtrosAplicados.ccb || undefined,
      recorte: statusFiltro === "TODOS" ? undefined : recorteDoStatusFiltroEncerrado(statusFiltro),
      renegociacao: filtrosAplicados.renegociacao || undefined,
    }),
    [pagina, pageSize, filtrosAplicados, statusFiltro]
  );

  const { data, isLoading, isError, isFetching } = useQuery<IVinculosEncerradosRetorno>({
    queryKey: [VINCULOS_ENCERRADOS_QUERY_KEY, params],
    queryFn: () => buscarVinculosEncerrados(params),
    placeholderData: (prev) => prev,
    retry: 0,
    refetchOnWindowFocus: false,
  });

  const ccbs = useMemo(() => (data?.data ?? []).map(hidratarCcbEncerrado), [data]);

  const handleSetStatusFiltro = (status: StatusFiltroEncerrado) => {
    setStatusFiltro(status);
    setPagina(1);
  };
  const handleFiltrar = () => {
    setFiltrosAplicados(filtrosDraft);
    setPagina(1);
  };
  const handleLimparFiltros = () => {
    setFiltrosDraft(FILTROS_INICIAIS);
    setFiltrosAplicados(FILTROS_INICIAIS);
    setStatusFiltro("TODOS");
    setPagina(1);
  };
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPagina(1);
  };
  // Reprocessamento aceito: o job roda em background — só invalida a listagem.
  const handleReprocessado = () =>
    queryClient.invalidateQueries({ queryKey: [VINCULOS_ENCERRADOS_QUERY_KEY] });

  const paginacao = (
    <Pagination
      currentPage={data?.pagina ?? 1}
      totalPages={data?.total_paginas ?? 1}
      pageSize={pageSize}
      totalItems={data?.total ?? ccbs.length}
      onPageChange={setPagina}
      onPageSizeChange={handlePageSizeChange}
      itemLabel="tomador"
      itemLabelPlural="tomadores"
    />
  );

  return (
    <Box w="100%" padding={{ base: "2", md: "4" }}>
      <Flex flexDirection="column" alignItems="center" width="100%">
        <Box
          {...backgroundStyle}
          borderRadius="md"
          p={{ base: "4", md: "6" }}
          color="white"
          boxShadow="md"
          width="100%"
          mb={6}
        >
          <Flex
            alignItems="center"
            justifyContent="space-between"
            gap={3}
            flexWrap="wrap"
            mb={{ base: "4", md: "6" }}
          >
            <Flex alignItems="center" gap={3} flexWrap="wrap">
              <Flex alignItems="center">
                <MdOutlineAttachMoney size="2rem" />
                <Text fontSize={{ base: "2xl", md: "4xl" }} fontWeight="bold" ml={2} fontFamily="Lexend Deca">
                  Vinculos Encerrados
                </Text>
              </Flex>
              <ReprocessarCobrancasButton onReprocessado={handleReprocessado} />
            </Flex>
            <ComoUtilizarModal
              tituloTela="Vínculos Encerrados"
              passos={PASSOS_COMO_UTILIZAR}
              conceitos={CONCEITOS_ESCRITURACAO_REPASSE}
              slidesExtras={[...SLIDES_RELATORIOS_PARCELAS, SLIDE_GERACAO_BOLETOS]}
            />
          </Flex>

          <VStack align="stretch" spacing={4}>
            <StatusFilterBarEncerrados
              statusFiltro={statusFiltro}
              setStatusFiltro={handleSetStatusFiltro}
            />
            <VinculosFiltersBar
              filtrosDraft={filtrosDraft}
              setFiltrosDraft={setFiltrosDraft}
              onFiltrar={handleFiltrar}
              onLimparFiltros={handleLimparFiltros}
              mostrarFiltroEmpresa={false}
              mostrarFiltroRenegociacao
            />
          </VStack>
        </Box>

        {isLoading && (
          <Flex w="100%" minH="40vh" justifyContent="center" alignItems="center">
            <Spinner size="lg" color="primary" />
          </Flex>
        )}

        {isError && (
          <Flex w="100%" minH="40vh" justifyContent="center" alignItems="center">
            <Text fontSize="xl" fontFamily="Lexend Deca" color="gray.400" textAlign="center">
              Não foi possível carregar os vínculos encerrados.
            </Text>
          </Flex>
        )}

        {!isLoading && !isError && ccbs.length === 0 && (
          <Flex w="100%" minH="40vh" justifyContent="center" alignItems="center">
            <Text fontSize="xl" fontFamily="Lexend Deca" color="gray.400" textAlign="center">
              Nenhum vínculo encontrado
            </Text>
          </Flex>
        )}

        {!isLoading && !isError && ccbs.length > 0 && (
          <>
            {paginacao}

            <VStack align="stretch" spacing={5} width="100%" opacity={isFetching ? 0.6 : 1}>
              {ccbs.map((ccb) => (
                <CcbRow key={ccb.id} ccb={ccb} tamanhoDestaque="md" elegibilidadeSeguro={ccb} />
              ))}
            </VStack>

            {paginacao}
          </>
        )}
      </Flex>
    </Box>
  );
}
